import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect } from "react";

interface ParamActionConfig {
  paramKey: string;
  expectedValue: string;
  action: () => void;
}

const useActionFromParams = (
  paramActions: ParamActionConfig[],
  extraKeysToClean?: string[],
) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  // Cleanup function to remove specified query parameters from the URL
  const cleanup = useCallback(() => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));

    // Iterate through all configured actions and remove their corresponding parameters
    paramActions.forEach(({ paramKey }) => {
      params.delete(paramKey);
    });

    if (extraKeysToClean && extraKeysToClean.length > 0) {
      extraKeysToClean.forEach((paramKey) => {
        params.delete(paramKey);
      });
    }
    // Replace the current URL with the updated query parameters without reloading the page
    router.replace(`?${params.toString()}`, { scroll: false });
  }, [searchParams, router]);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    paramActions.forEach(({ paramKey, expectedValue, action }) => {
      const paramValue = searchParams.get(paramKey);
      if (paramValue === expectedValue) {
        const timeout = setTimeout(action, 400);
        timeouts.push(timeout);
      }
    });
    return () => timeouts.forEach(clearTimeout);
  }, [searchParams, paramActions]);

  return { cleanup };
};

export default useActionFromParams;
